import {
  Check,
  ChevronDown,
  ChevronLeft,
  Loader2,
  Pencil,
  Settings,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useListQuestionnaires } from '../api/useListQuestionnaires';

/** What the header reports about the autosave, so the admin can see their work is safe. */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * The builder's header, to the designer's layout:
 *
 *   ‹  Questionnaire Name ✎   [Change Questionnaire ▾]   …   [Publish]  ⚙  🗑
 *
 * WHAT THIS REPLACED, and why each piece went:
 *
 *   • "Questionnaires" as the title — every questionnaire's builder had the same heading, so nothing
 *     on screen said which one was open. The name is the title, and editable in place.
 *   • A separate row below the header holding Name and Description inputs. That row cost ~70px of
 *     vertical space on every builder, permanently, to hold two fields an admin touches once. The
 *     name is now the title; the description lives behind the gear.
 *   • A Save button — replaced by autosave, so there is nothing to forget to press. `Publish` takes
 *     its place, which is the action that actually changes anything for applicants.
 *   • The notifications bell and profile avatar. Neither is in the designer's chrome, and Log Out
 *     moved to the foot of the sidebar.
 */
export function QuestionnaireBuilderHeader({
  questionnaireId,
  name,
  description,
  saveState,
  onNameChange,
  onDescriptionChange,
  onPublish,
  onDelete,
  isPublishing,
}: {
  questionnaireId: string | undefined;
  name: string;
  description: string;
  saveState: SaveState;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onPublish: () => void;
  onDelete: () => void;
  isPublishing: boolean;
}) {
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Re-seeded whenever editing STARTS, not on every name change — seeding on every render would
  // overwrite what is being typed with the committed value on each keystroke.
  useEffect(() => {
    if (editingName) setDraftName(name);
  }, [editingName, name]);

  const { data } = useListQuestionnaires({ page: 1, pageSize: 100 });
  const others = (data?.items ?? []).filter((q) => q.id !== questionnaireId);

  function commitName() {
    const trimmed = draftName.trim();
    // An empty name is rejected rather than saved: `Name` is required on the server, so an empty
    // autosave would fail validation and the admin would see a failing save with no explanation.
    if (trimmed.length > 0 && trimmed !== name) onNameChange(trimmed);
    setEditingName(false);
  }

  return (
    <div className="flex h-[60px] shrink-0 items-center gap-3 border-b border-grayEF bg-white px-4">
      <Link
        to="/questionnaires"
        className="flex shrink-0 items-center justify-center text-[#4B5563] transition-colors hover:text-primary"
        aria-label="Back to questionnaires"
      >
        <ChevronLeft size={20} />
      </Link>

      {editingName ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <input
            type="text"
            value={draftName}
            maxLength={200}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              // Escape ABANDONS the edit — without it the only way out of the field is to commit,
              // so a mistyped name cannot be undone except by retyping the old one.
              if (e.key === 'Escape') setEditingName(false);
            }}
            // biome-ignore lint/a11y/noAutofocus: the field only exists because the pencil was clicked to type in it.
            autoFocus
            aria-label="Questionnaire name"
            className="rounded border border-primary bg-white px-2.5 py-1 text-[16px] font-semibold text-[#0F172A] shadow-xs outline-none"
          />
          <button
            type="button"
            onClick={commitName}
            aria-label="Save name"
            className="rounded p-1 text-primary transition-colors hover:bg-blue-50"
          >
            <Check size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditingName(true)}
          title="Rename"
          className="group flex min-w-0 items-center gap-1.5"
        >
          <span className="truncate text-[18px] font-semibold tracking-tight text-[#0F172A]">
            {name || 'Untitled Questionnaire'}
          </span>
          <Pencil
            size={15}
            className="shrink-0 text-[#64748B] transition-colors group-hover:text-primary"
          />
        </button>
      )}

      {/* Jump to another questionnaire without going back to the list — the designer's
          "Change Questionnaire". */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-[13px] font-medium text-[#334155] transition-colors hover:border-primary hover:text-primary"
          >
            <span>Change Questionnaire</span>
            <ChevronDown size={14} className="text-[#64748B]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
          <div className="border-b border-[#F1F5F9] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
            Select Questionnaire
          </div>
          {others.length === 0 ? (
            <div className="px-3.5 py-3 text-[13px] text-gray66">No other questionnaires yet.</div>
          ) : (
            others.map((q) => (
              <DropdownMenuItem
                key={q.id}
                onClick={() => navigate(`/questionnaires/${q.id}/builder`)}
                className="flex items-center justify-between px-3.5 py-2 text-[13px] text-[#334155]"
              >
                <span className="truncate">{q.name}</span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="ms-auto flex shrink-0 items-center gap-3">
        <SaveIndicator state={saveState} />

        {/* ALWAYS "Publish" — one button, one label, as the designer's header has it. It used to flip
            to "Unpublish" once live, which meant the primary action changed identity under the admin
            and the button they had just pressed now offered to undo itself.

            On an already-published questionnaire it is still meaningful rather than a no-op: edits
            autosave continuously, so pressing Publish flushes the pending save and republishes the
            current state. Taking a questionnaire back to Draft or Archived is done from the status
            pill on the list, which is where every other status change lives. */}
        <button
          type="button"
          onClick={onPublish}
          disabled={isPublishing}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-white transition-colors hover:bg-[#0038CC] disabled:opacity-50"
        >
          {isPublishing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          <span>Publish</span>
        </button>

        {/* The description, and anything else about the questionnaire itself rather than its
            questions. Behind the gear so it does not occupy a permanent row. */}
        <DropdownMenu open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Questionnaire settings"
              title="Settings"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748B] transition-colors hover:bg-grayEF hover:text-primary"
            >
              <Settings size={17} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-3">
            <label className="block">
              <span className="mb-1.5 block text-[length:var(--text-12)] font-medium text-black">
                Description
              </span>
              <textarea
                value={description}
                maxLength={1000}
                rows={3}
                placeholder="What this questionnaire is for (internal)"
                onChange={(e) => onDescriptionChange(e.target.value)}
                className="w-full resize-none rounded-lg border border-placeholder bg-white px-3 py-2 text-[length:var(--text-14)] text-[#323338] outline-none transition-all placeholder:text-[#94A3B8] focus:border-primary"
              />
            </label>
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete questionnaire"
          title="Delete"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-destructive transition-colors hover:bg-red-50"
        >
          <Trash2 size={17} />
        </button>
      </div>
    </div>
  );
}

/**
 * Autosave status.
 *
 * Present because autosave without feedback is indistinguishable from no save at all — an admin who
 * cannot see that their work was stored will either press something or lose confidence in the page.
 * "Saved" is shown rather than hidden on success for the same reason.
 */
function SaveIndicator({ state }: { state: SaveState }) {
  if (state === 'idle') {
    return null;
  }

  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-[length:var(--text-12)] text-gray66">
        <Loader2 size={12} className="animate-spin" />
        Saving…
      </span>
    );
  }

  if (state === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-[length:var(--text-12)] font-medium text-destructive">
        Not saved — retrying
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-[length:var(--text-12)] text-green00">
      <Check size={12} />
      Saved
    </span>
  );
}
